'use client';

import React from 'react';
import { Brand } from '@/domainLogic/index.ts';
import type { HeaderProps as ContentHubHeaderProps } from '@lmn/contenthub-shared/components';
import { InnerSlot } from '@lmn/common-search-widget';
import ExternalHeader from '@/app/common/ExternalHeader.tsx';
import HtmlInjector from '@/utils/HTMLInjector.tsx';
import ContentHubHeader from '@/app/common/headerFooter/ContentHubHeader.tsx';
import StaticHeader from '@/app/common/headerFooter/StaticHeader.tsx';
import { isHeaderPropsValid } from '@/app/common/headerFooter/headerFooterUtils.ts';
import {
  CSWAppContainer,
  CSWAppExpandedOffsetReset,
  CSWExternalContainer,
  ExternalHeaderSpacing,
} from '@/app/common/styled.ts';
import CSW from '@/app/common/CSW/CSW';
import type { CSWProps } from '@/app/common/CSW/services/types.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';

export type HeaderManagerProps = {
  externalHeaderEnabled: boolean;
  externalHeaderHTML: string | null;
  showExternalCSW: boolean;
  appShowCsw: boolean;
  brand: Brand;
  contentHubHeaderProps?: ContentHubHeaderProps;
  cswProps: CSWProps;
  headerFooterStaticEnabled?: boolean;
};

const HeaderManager = ({
  externalHeaderEnabled,
  externalHeaderHTML,
  showExternalCSW,
  appShowCsw,
  brand,
  contentHubHeaderProps,
  cswProps,
  headerFooterStaticEnabled,
}: HeaderManagerProps) => {
  const showHolidayPiratesHeader = brand === Brand.HOLIDAYPIRATES;
  const showHolidayGuruHeader = brand === Brand.HOLIDAYGURU;
  const { commonData } = usePageWrapperData();
  const { isApp } = commonData;

  if (externalHeaderEnabled) {
    return (
      <ExternalHeaderSpacing brand={brand} clearFixedHeader={!showHolidayGuruHeader} isApp={isApp}>
        {showHolidayPiratesHeader && <ExternalHeader externalHeaderHTML={externalHeaderHTML} />}
        {showHolidayGuruHeader && <HtmlInjector htmlString={externalHeaderHTML} />}
        {showExternalCSW && (
          <CSWExternalContainer brand={brand}>
            <InnerSlot setIsSearchCollapsed={() => {}}>
              <CSW {...cswProps} />
            </InnerSlot>
          </CSWExternalContainer>
        )}
      </ExternalHeaderSpacing>
    );
  }

  // MPH-395 workaround (see CSWAppContainer in styled.ts): render the CSW outside
  // the ContentHub nav so the app's injected `display: none` can't reach it.
  if (isApp && appShowCsw) {
    return (
      <>
        <CSWAppExpandedOffsetReset />
        <CSWAppContainer>
          <InnerSlot setIsSearchCollapsed={() => {}}>
            <CSW {...cswProps} />
          </InnerSlot>
        </CSWAppContainer>
      </>
    );
  }

  if (contentHubHeaderProps && isHeaderPropsValid(contentHubHeaderProps)) {
    return (
      <ContentHubHeader {...contentHubHeaderProps} innerSlot={<CSW {...cswProps} />} InnerSlotComponent={InnerSlot} />
    );
  }

  if (headerFooterStaticEnabled) return <StaticHeader />;

  return null;
};

export default HeaderManager;
